import { useCallback, useState } from 'react'
import useErrorModal from '@hooks/useErrorModal'
import apiErrorHandler from '@utils/apiErrorHandler'

interface UseApiActionOptions<TBad> {
  // システムエラー時の基本文言（例: '保存できませんでした。'）。status は自動付与。
  systemMessage: string
  // 通信エラー時の文言。既定 'ネットワークを確認してください。'。
  networkMessage?: string
  // 400 本文の処理。指定時は 400 をここで処理しシステムエラーは出さない。
  onBadRequest?: (body: TBad) => void
}

// run の戻り値。成功と「ハンドリング済みエラー」を判別できるよう discriminated union にする
// （204 等で成功値が undefined でも誤判定しない）。
export type ApiActionResult<TOk> = { ok: true; data: TOk } | { ok: false }

// useApiEffect（deps 駆動 get 用）の命令型シブリング。
// ボタン/ハンドラ起点の jsonApi 呼び出しで、エラー分類・disableAction・400分岐・
// skip-on-401 を全てフック内に封じ込める。呼び出し側は systemMessage と成功処理だけ書く。
//
//   const { run, disableAction } = useApiAction({ systemMessage: '保存できませんでした。' });
//   run(() => jsonApi(url, 'put', data)).then((r) => { if (r.ok) Turbolinks.visit(...); });
//
// エラー側の分類はテスト済みの apiErrorHandler に完全委譲する。
const useApiAction = <TOk = unknown, TBad = unknown>(options: UseApiActionOptions<TBad>) => {
  const [disableAction, setDisableAction] = useState(false)
  const showError = useErrorModal()
  const { systemMessage, networkMessage, onBadRequest } = options

  const run = useCallback(
    (request: () => Promise<TOk>): Promise<ApiActionResult<TOk>> => {
      setDisableAction(true)
      return request().then(
        (data): ApiActionResult<TOk> => {
          setDisableAction(false)
          return { ok: true, data }
        },
        (error: unknown): ApiActionResult<TOk> => {
          apiErrorHandler<TBad>({
            showError,
            systemMessage,
            networkMessage,
            onBadRequest,
            onFinally: () => setDisableAction(false),
          })(error)
          return { ok: false }
        }
      )
    },
    [showError, systemMessage, networkMessage, onBadRequest]
  )

  return { run, disableAction, setDisableAction }
}

export default useApiAction
